import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

export default function Dashboard() {
  const { profil } = useAuth();
  const navigate = useNavigate();
  const isTemsilci = profil?.rol === 'temsilci';
  const isDepocu = profil?.rol === 'depocu';

  if (isTemsilci) return <TemsilciDashboard profil={profil} navigate={navigate} />;
  if (isDepocu) return <DepocuDashboard profil={profil} navigate={navigate} />;
  return <AdminDashboard profil={profil} navigate={navigate} />;
}

// ─── DEPOCU DASHBOARD ───
function DepocuDashboard({ profil, navigate }) {
  const [siparisler, setSiparisler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ kargoBekleyen: 0, bugunKargolanan: 0 });

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    const bugun = new Date().toISOString().split('T')[0];
    const [bekleyen, kargolanan, son] = await Promise.all([
      supabase.from('siparisler').select('id', { count: 'exact' }).eq('siparis_durumu', 'hazirlaniyor'),
      supabase.from('siparisler').select('id', { count: 'exact' }).eq('siparis_durumu', 'kargoda').gte('updated_at', bugun),
      supabase.from('siparisler').select('*, musteriler(ad, sehir), firmalar(ad)').in('siparis_durumu', ['alinidi', 'hazirlaniyor']).order('created_at', { ascending: false }).limit(20),
    ]);
    setStats({ kargoBekleyen: bekleyen.count || 0, bugunKargolanan: kargolanan.count || 0 });
    setSiparisler(son.data || []);
    setLoading(false);
  }

  const durumBadge = { alinidi: <span className="badge badge-blue">Alındı</span>, hazirlaniyor: <span className="badge badge-yellow">Hazırlanıyor</span>, kargoda: <span className="badge badge-orange">Kargoda</span>, teslim: <span className="badge badge-green">Teslim</span>, iade: <span className="badge badge-red">İade</span>, iptal: <span className="badge badge-gray">İptal</span> };

  if (loading) return <div className="loading">⏳ Yükleniyor...</div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Merhaba, {profil?.ad} 👋</h1>
          <p className="page-subtitle">{new Date().toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
      </div>

      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
        <div className="stat-card"><div className="stat-label">Kargo Bekleyen</div><div className="stat-value" style={{ color: 'var(--yellow)' }}>{stats.kargoBekleyen}</div></div>
        <div className="stat-card"><div className="stat-label">Bugün Kargolanan</div><div className="stat-value" style={{ color: 'var(--green)' }}>{stats.bugunKargolanan}</div></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { icon: '🛒', label: 'Siparişler', to: '/siparisler' },
          { icon: '🏭', label: 'Depolar', to: '/depolar' },
          { icon: '🔍', label: 'Arama', to: '/arama' },
        ].map(item => (
          <button key={item.to} className="card" onClick={() => navigate(item.to)}
            style={{ textAlign: 'center', cursor: 'pointer', padding: '16px 12px' }}
            onMouseOver={e => e.currentTarget.style.borderColor = 'var(--accent)'}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border)'}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>{item.icon}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>{item.label}</div>
          </button>
        ))}
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontWeight: 700 }}>
          Kargo Bekleyen Siparişler
        </div>
        <table className="table">
          <thead>
            <tr><th>Sipariş No</th><th>Müşteri</th><th>Şehir</th><th>Firma</th><th>Durum</th><th>Tarih</th></tr>
          </thead>
          <tbody>
            {siparisler.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30, color: 'var(--text2)' }}>Bekleyen sipariş yok</td></tr>
            ) : siparisler.map(s => (
              <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/siparisler/${s.id}`)}>
                <td><span className="mono" style={{ color: 'var(--accent)', fontWeight: 600 }}>{s.siparis_no}</span></td>
                <td>{s.musteriler?.ad || '-'}</td>
                <td style={{ color: 'var(--text2)' }}>{s.musteriler?.sehir || '-'}</td>
                <td style={{ color: 'var(--text2)' }}>{s.firmalar?.ad || '-'}</td>
                <td>{durumBadge[s.siparis_durumu]}</td>
                <td style={{ fontSize: 12, color: 'var(--text2)' }}>{new Date(s.created_at).toLocaleDateString('tr-TR')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── TEMSİLCİ DASHBOARD ───
function TemsilciDashboard({ profil, navigate }) {
  const [stats, setStats] = useState({ bugunSiparis: 0, bugunKomisyon: 0, bekleyenKomisyon: 0, odenmemisSiparis: 0, toplamMusteri: 0 });
  const [sonSiparisler, setSonSiparisler] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    const bugun = new Date().toISOString().split('T')[0];
    const [bugunS, odenmemis, musteriler, sonS, komisyonlar, odemeler] = await Promise.all([
      supabase.from('siparisler').select('toplam_komisyon').eq('temsilci_id', profil.id).gte('created_at', bugun),
      supabase.from('siparisler').select('id', { count: 'exact' }).eq('temsilci_id', profil.id).eq('odeme_durumu', 'odenmedi'),
      supabase.from('musteriler').select('id', { count: 'exact' }).eq('temsilci_id', profil.id),
      supabase.from('siparisler').select('*, musteriler(ad), firmalar(ad)').eq('temsilci_id', profil.id).order('created_at', { ascending: false }).limit(8),
      supabase.from('siparisler').select('toplam_komisyon').eq('temsilci_id', profil.id).not('siparis_durumu', 'eq', 'iptal'),
      supabase.from('komisyon_odemeleri').select('tutar').eq('temsilci_id', profil.id),
    ]);

    const bugunKomisyon = (bugunS.data || []).reduce((a, r) => a + (r.toplam_komisyon || 0), 0);
    const toplamKomisyon = (komisyonlar.data || []).reduce((a, r) => a + (r.toplam_komisyon || 0), 0);
    const toplamOdenen = (odemeler.data || []).reduce((a, r) => a + (r.tutar || 0), 0);

    setStats({
      bugunSiparis: bugunS.data?.length || 0,
      bugunKomisyon,
      bekleyenKomisyon: toplamKomisyon - toplamOdenen,
      odenmemisSiparis: odenmemis.count || 0,
      toplamMusteri: musteriler.count || 0,
    });
    setSonSiparisler(sonS.data || []);
    setLoading(false);
  }

  const odemeBadge = { odendi: <span className="badge badge-green">Ödendi</span>, odenmedi: <span className="badge badge-red">Ödenmedi</span>, kismi: <span className="badge badge-yellow">Kısmi</span> };
  const durumBadge = { alinidi: <span className="badge badge-blue">Alındı</span>, hazirlaniyor: <span className="badge badge-yellow">Hazırlanıyor</span>, kargoda: <span className="badge badge-orange">Kargoda</span>, teslim: <span className="badge badge-green">Teslim</span>, iade: <span className="badge badge-red">İade</span>, iptal: <span className="badge badge-gray">İptal</span> };

  if (loading) return <div className="loading">⏳ Yükleniyor...</div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Merhaba, {profil?.ad} 👋</h1>
          <p className="page-subtitle">{new Date().toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/siparisler/yeni')}>+ Yeni Sipariş</button>
      </div>

      <div className="stat-grid">
        <div className="stat-card"><div className="stat-label">Bugün Siparişim</div><div className="stat-value">{stats.bugunSiparis}</div></div>
        <div className="stat-card"><div className="stat-label">Bugün Komisyonum</div><div className="stat-value" style={{ fontSize: 18, color: 'var(--yellow)' }}>{stats.bugunKomisyon.toLocaleString('tr-TR')} ₺</div></div>
        <div className="stat-card"><div className="stat-label">Bekleyen Komisyon</div><div className="stat-value" style={{ fontSize: 18, color: stats.bekleyenKomisyon > 0 ? 'var(--red)' : 'var(--green)' }}>{stats.bekleyenKomisyon.toLocaleString('tr-TR')} ₺</div></div>
        <div className="stat-card"><div className="stat-label">Ödenmemiş Sipariş</div><div className="stat-value" style={{ color: stats.odenmemisSiparis > 0 ? 'var(--red)' : 'var(--text)' }}>{stats.odenmemisSiparis}</div></div>
        <div className="stat-card"><div className="stat-label">Müşterim</div><div className="stat-value">{stats.toplamMusteri}</div></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { icon: '🛒', label: 'Siparişlerim', to: '/siparisler' },
          { icon: '👥', label: 'Müşterilerim', to: '/musteriler' },
          { icon: '💰', label: 'Komisyonlarım', to: '/komisyonlar' },
          { icon: '🔍', label: 'Arama', to: '/arama' },
        ].map(item => (
          <button key={item.to} className="card" onClick={() => navigate(item.to)}
            style={{ textAlign: 'center', cursor: 'pointer', padding: '16px 12px' }}
            onMouseOver={e => e.currentTarget.style.borderColor = 'var(--accent)'}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border)'}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>{item.icon}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>{item.label}</div>
          </button>
        ))}
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700 }}>Son Siparişlerim</span>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/siparisler')}>Tümünü Gör →</button>
        </div>
        <table className="table">
          <thead>
            <tr><th>Sipariş No</th><th>Müşteri</th><th>Firma</th><th>Tutar</th><th>Durum</th><th>Ödeme</th><th>Tarih</th></tr>
          </thead>
          <tbody>
            {sonSiparisler.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 30, color: 'var(--text2)' }}>Henüz sipariş yok</td></tr>
            ) : sonSiparisler.map(s => (
              <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/siparisler/${s.id}`)}>
                <td><span className="mono" style={{ color: 'var(--accent)', fontWeight: 600 }}>{s.siparis_no}</span></td>
                <td>{s.musteriler?.ad || '-'}</td>
                <td style={{ color: 'var(--text2)' }}>{s.firmalar?.ad || '-'}</td>
                <td style={{ fontWeight: 600 }}>{(s.toplam_tutar || 0).toLocaleString('tr-TR')} ₺</td>
                <td>{durumBadge[s.siparis_durumu]}</td>
                <td>{odemeBadge[s.odeme_durumu]}</td>
                <td style={{ fontSize: 12, color: 'var(--text2)' }}>{new Date(s.created_at).toLocaleDateString('tr-TR')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── ADMİN DASHBOARD ───
function AdminDashboard({ profil, navigate }) {
  const [stats, setStats] = useState({ bugunSiparis: 0, bugunGelir: 0, odenmemis: 0, kargoBekleyen: 0, toplamMusteri: 0 });
  const [sonSiparisler, setSonSiparisler] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    const bugun = new Date().toISOString().split('T')[0];
    const [siparisler, odenmemis, kargo, musteriler] = await Promise.all([
      supabase.from('siparisler').select('toplam_tutar, created_at').gte('created_at', bugun),
      supabase.from('siparisler').select('id', { count: 'exact' }).eq('odeme_durumu', 'odenmedi'),
      supabase.from('siparisler').select('id', { count: 'exact' }).eq('siparis_durumu', 'hazirlaniyor'),
      supabase.from('musteriler').select('id', { count: 'exact' }).eq('aktif', true),
    ]);
    const bugunGelir = (siparisler.data || []).reduce((s, r) => s + (r.toplam_tutar || 0), 0);
    const { data: son } = await supabase.from('siparisler').select('*, musteriler(ad), firmalar(ad)').order('created_at', { ascending: false }).limit(8);
    setStats({ bugunSiparis: siparisler.data?.length || 0, bugunGelir, odenmemis: odenmemis.count || 0, kargoBekleyen: kargo.count || 0, toplamMusteri: musteriler.count || 0 });
    setSonSiparisler(son || []);
    setLoading(false);
  }

  async function handleSil(e, siparisId) {
    e.stopPropagation();
    if (!window.confirm('Bu siparişi silmek istediğinizden emin misiniz?')) return;
    await supabase.from('siparis_kalemleri').delete().eq('siparis_id', siparisId);
    await supabase.from('siparisler').delete().eq('id', siparisId);
    fetchData();
  }

  const durumBadge = { alinidi: <span className="badge badge-blue">Alındı</span>, hazirlaniyor: <span className="badge badge-yellow">Hazırlanıyor</span>, kargoda: <span className="badge badge-orange">Kargoda</span>, teslim: <span className="badge badge-green">Teslim</span>, iade: <span className="badge badge-red">İade</span>, iptal: <span className="badge badge-gray">İptal</span> };
  const odemeBadge = { odendi: <span className="badge badge-green">Ödendi</span>, odenmedi: <span className="badge badge-red">Ödenmedi</span>, kismi: <span className="badge badge-yellow">Kısmi</span>, vadeli: <span className="badge badge-purple">Vadeli</span> };

  if (loading) return <div className="loading">⏳ Yükleniyor...</div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">{new Date().toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/siparisler/yeni')}>+ Yeni Sipariş</button>
      </div>

      <div className="stat-grid">
        <div className="stat-card"><div className="stat-label">Bugün Sipariş</div><div className="stat-value">{stats.bugunSiparis}</div></div>
        <div className="stat-card"><div className="stat-label">Bugün Gelir</div><div className="stat-value" style={{ color: 'var(--green)', fontSize: 18 }}>{stats.bugunGelir.toLocaleString('tr-TR')} ₺</div></div>
        <div className="stat-card"><div className="stat-label">Ödenmemiş</div><div className="stat-value" style={{ color: 'var(--red)' }}>{stats.odenmemis}</div></div>
        <div className="stat-card"><div className="stat-label">Kargo Bekleyen</div><div className="stat-value" style={{ color: 'var(--yellow)' }}>{stats.kargoBekleyen}</div></div>
        <div className="stat-card"><div className="stat-label">Toplam Müşteri</div><div className="stat-value">{stats.toplamMusteri}</div></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { icon: '🛒', label: 'Siparişler', to: '/siparisler' },
          { icon: '🔍', label: 'Arama', to: '/arama' },
          { icon: '👥', label: 'Müşteriler', to: '/musteriler' },
          { icon: '📦', label: 'Ürünler', to: '/urunler' },
          { icon: '🏭', label: 'Depolar', to: '/depolar' },
          { icon: '💰', label: 'Komisyonlar', to: '/komisyonlar' },
          { icon: '💳', label: 'Ödemeler', to: '/odemeler' },
          { icon: '📈', label: 'Raporlar', to: '/raporlar' },
        ].map(item => (
          <button key={item.to} className="card" onClick={() => navigate(item.to)}
            style={{ textAlign: 'center', cursor: 'pointer', padding: '16px 12px' }}
            onMouseOver={e => e.currentTarget.style.borderColor = 'var(--accent)'}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border)'}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>{item.icon}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>{item.label}</div>
          </button>
        ))}
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700 }}>Son Siparişler</span>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/siparisler')}>Tümünü Gör →</button>
        </div>
        <table className="table">
          <thead>
            <tr><th>Sipariş No</th><th>Müşteri</th><th>Firma</th><th>Tutar</th><th>Durum</th><th>Ödeme</th><th>Tarih</th><th></th></tr>
          </thead>
          <tbody>
            {sonSiparisler.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 30, color: 'var(--text2)' }}>Henüz sipariş yok</td></tr>
            ) : sonSiparisler.map(s => (
              <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/siparisler/${s.id}`)}>
                <td><span className="mono" style={{ color: 'var(--accent)', fontWeight: 600 }}>{s.siparis_no}</span></td>
                <td>{s.musteriler?.ad || '-'}</td>
                <td style={{ color: 'var(--text2)' }}>{s.firmalar?.ad || '-'}</td>
                <td style={{ fontWeight: 600 }}>{(s.toplam_tutar || 0).toLocaleString('tr-TR')} ₺</td>
                <td>{durumBadge[s.siparis_durumu]}</td>
                <td>{odemeBadge[s.odeme_durumu]}</td>
                <td style={{ fontSize: 12, color: 'var(--text2)' }}>{new Date(s.created_at).toLocaleDateString('tr-TR')}</td>
                <td onClick={e => e.stopPropagation()}>
                  <button className="btn btn-danger btn-sm" onClick={e => handleSil(e, s.id)}>Sil</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
